/**
 * ExplainabilityLauncher — 4-step wizard to launch a SHAP explanation run.
 * Phase 4.10 β5 — WQ-240.
 *
 * Steps:
 *   1. Choose source mode — single prediction OR batch
 *   2. Pick the target ID
 *      - single: pick from recent predictions OR enter prediction id manually
 *      - batch: enter batch id
 *   3. Configure parameters — max_features (single + batch); sample_size (batch only)
 *   4. Review & Launch
 *
 * Wraps existing POST /api/admin/ai/explain/prediction/{id} and
 * POST /api/admin/ai/explain/batch/{id}. Method is SHAP (the only supported
 * backend method); no method selector — keeps wizard honest about backend.
 *
 * Pattern reuse: WQ-237 AutoMLWizard + WQ-238 PredictionRequestForm
 * (copy-paste-with-divergence per WQ-237 NOTES).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  runPredictionExplanation,
  runBatchExplanation,
  listExplainablePredictions,
} from '../../services/explainabilityService';
import WizardModalShell from '../wizard/WizardModalShell';

type WizardStep = 1 | 2 | 3 | 4;
type SourceMode = 'single' | 'batch';

interface ExplainabilityLauncherProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: (resultKey: string) => void;
}

interface PredictionOption {
  id: string;
  model_id: string;
  created_at: string;
  batch_id: string | null;
}

const STEP_TITLES: Record<WizardStep, string> = {
  1: 'Choose source mode',
  2: 'Pick target',
  3: 'Configure parameters',
  4: 'Review & launch',
};

const ExplainabilityLauncher: React.FC<ExplainabilityLauncherProps> = ({ isOpen, onClose, onSubmitted }) => {
  const { authFetch } = useAuth();
  const [step, setStep] = useState<WizardStep>(1);

  // Step 1 state
  const [sourceMode, setSourceMode] = useState<SourceMode>('single');

  // Step 2 state — single mode
  const [predictions, setPredictions] = useState<PredictionOption[]>([]);
  const [predictionsLoading, setPredictionsLoading] = useState(false);
  const [predictionsError, setPredictionsError] = useState<string | null>(null);
  const [selectedPredictionId, setSelectedPredictionId] = useState<string>('');
  const [manualPredictionId, setManualPredictionId] = useState<string>('');

  // Step 2 state — batch mode
  const [batchId, setBatchId] = useState<string>('');

  // Step 3 state
  const [maxFeatures, setMaxFeatures] = useState<number>(10);
  const [sampleSize, setSampleSize] = useState<number>(50);

  // Submit state
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // Reset wizard each time it opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSourceMode('single');
      setSelectedPredictionId('');
      setManualPredictionId('');
      setBatchId('');
      setMaxFeatures(10);
      setSampleSize(50);
      setLaunchError(null);
    }
  }, [isOpen]);

  // Load predictions when wizard opens (only useful for single mode)
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setPredictionsLoading(true);
      setPredictionsError(null);
      try {
        const data = await listExplainablePredictions(authFetch);
        if (cancelled) return;
        setPredictions(data);
      } catch (err) {
        if (!cancelled) setPredictionsError(err instanceof Error ? err.message : 'Failed to load predictions');
      } finally {
        if (!cancelled) setPredictionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, authFetch]);

  const effectivePredictionId = useMemo(
    () => (manualPredictionId.trim().length > 0 ? manualPredictionId.trim() : selectedPredictionId),
    [manualPredictionId, selectedPredictionId]
  );

  const canAdvance = useMemo(() => {
    if (step === 1) return !!sourceMode;
    if (step === 2) {
      if (sourceMode === 'single') return effectivePredictionId.length > 0;
      return batchId.trim().length > 0;
    }
    if (step === 3) {
      if (maxFeatures < 1 || maxFeatures > 100) return false;
      if (sourceMode === 'batch' && (sampleSize < 1 || sampleSize > 1000)) return false;
      return true;
    }
    return false;
  }, [step, sourceMode, effectivePredictionId, batchId, maxFeatures, sampleSize]);

  const handleNext = () => {
    if (!canAdvance) return;
    setStep((prev) => (prev < 4 ? ((prev + 1) as WizardStep) : prev));
  };

  const handleBack = () => {
    setStep((prev) => (prev > 1 ? ((prev - 1) as WizardStep) : prev));
  };

  const handleLaunch = async () => {
    setLaunching(true);
    setLaunchError(null);
    try {
      if (sourceMode === 'single') {
        const result = await runPredictionExplanation(authFetch, effectivePredictionId, { max_features: maxFeatures });
        if (onSubmitted) onSubmitted(effectivePredictionId);
        // result intentionally not propagated — onSubmitted is the contract
        void result;
      } else {
        const result = await runBatchExplanation(authFetch, batchId.trim(), { sample_size: sampleSize, max_features: maxFeatures });
        if (onSubmitted) onSubmitted(result.batch_id);
      }
      onClose();
    } catch (err) {
      setLaunchError(err instanceof Error ? err.message : 'Failed to launch explanation');
    } finally {
      setLaunching(false);
    }
  };

  return (
    <WizardModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Run SHAP Explanation"
      totalSteps={4}
      currentStep={step}
      stepTitle={STEP_TITLES[step]}
      testIdPrefix="explain-launcher"
      canAdvance={canAdvance}
      submitting={launching}
      onBack={handleBack}
      onNext={handleNext}
      onSubmit={handleLaunch}
      launchError={launchError}
      maxWidthPx={680}
    >
      <>
          {step === 1 && (
            <div data-testid="explain-launcher-step-1">
              <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                Pick how you'd like to compute explanations.
              </div>
              <div role="radiogroup" data-testid="explain-launcher-mode-group">
                {(['single', 'batch'] as SourceMode[]).map((m) => (
                  <label
                    key={m}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                      borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                      border: sourceMode === m ? '1px solid var(--accent-primary)' : '1px solid var(--border)',
                      background: sourceMode === m ? 'rgba(59,130,246,0.05)' : 'var(--bg-secondary)',
                    }}
                  >
                    <input
                      type="radio"
                      name="explain-launcher-mode"
                      data-testid={`explain-launcher-mode-${m}`}
                      value={m}
                      checked={sourceMode === m}
                      onChange={() => setSourceMode(m)}
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>
                        {m === 'single' ? 'Single prediction' : 'Batch'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        {m === 'single'
                          ? 'Compute SHAP for one prediction by ID.'
                          : 'Aggregate SHAP feature importance across a sampled batch.'}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 2 && sourceMode === 'single' && (
            <div data-testid="explain-launcher-step-2-single">
              {predictionsLoading && (
                <div data-testid="explain-launcher-predictions-loading" style={{ color: 'var(--text-secondary)' }}>
                  Loading recent predictions...
                </div>
              )}
              {predictionsError && (
                <div data-testid="explain-launcher-predictions-error" style={{
                  padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 12,
                }}>
                  {predictionsError}
                </div>
              )}
              {predictions.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                    Recent predictions
                  </label>
                  <select
                    data-testid="explain-launcher-prediction-select"
                    value={selectedPredictionId}
                    onChange={(e) => { setSelectedPredictionId(e.target.value); setManualPredictionId(''); }}
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: 6,
                      border: '1px solid var(--border)', background: 'var(--bg-primary)',
                      color: 'var(--text-primary)', fontSize: 13,
                    }}
                  >
                    <option value="">Pick a prediction...</option>
                    {predictions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.id.slice(0, 8)} · model {p.model_id.slice(0, 8)} · {p.created_at.slice(0, 16)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Or enter a prediction ID manually
                </label>
                <input
                  type="text"
                  data-testid="explain-launcher-prediction-manual"
                  value={manualPredictionId}
                  onChange={(e) => { setManualPredictionId(e.target.value); setSelectedPredictionId(''); }}
                  placeholder="prediction-id-uuid"
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 6,
                    border: '1px solid var(--border)', background: 'var(--bg-primary)',
                    color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace',
                  }}
                />
              </div>
            </div>
          )}

          {step === 2 && sourceMode === 'batch' && (
            <div data-testid="explain-launcher-step-2-batch">
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                Batch ID *
              </label>
              <input
                type="text"
                data-testid="explain-launcher-batch-id"
                value={batchId}
                onChange={(e) => setBatchId(e.target.value)}
                placeholder="batch-id-uuid"
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 6,
                  border: '1px solid var(--border)', background: 'var(--bg-primary)',
                  color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace',
                }}
              />
            </div>
          )}

          {step === 3 && (
            <div data-testid="explain-launcher-step-3">
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Max features to return *
                </label>
                <input
                  type="number"
                  data-testid="explain-launcher-max-features"
                  min={1}
                  max={100}
                  value={maxFeatures}
                  onChange={(e) => setMaxFeatures(parseInt(e.target.value) || 0)}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 6,
                    border: '1px solid var(--border)', background: 'var(--bg-primary)',
                    color: 'var(--text-primary)', fontSize: 13,
                  }}
                />
                {(maxFeatures < 1 || maxFeatures > 100) && (
                  <div data-testid="explain-launcher-max-features-error" style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                    Max features must be between 1 and 100.
                  </div>
                )}
              </div>
              {sourceMode === 'batch' && (
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                    Sample size *
                  </label>
                  <input
                    type="number"
                    data-testid="explain-launcher-sample-size"
                    min={1}
                    max={1000}
                    value={sampleSize}
                    onChange={(e) => setSampleSize(parseInt(e.target.value) || 0)}
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: 6,
                      border: '1px solid var(--border)', background: 'var(--bg-primary)',
                      color: 'var(--text-primary)', fontSize: 13,
                    }}
                  />
                  {(sampleSize < 1 || sampleSize > 1000) && (
                    <div data-testid="explain-launcher-sample-size-error" style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                      Sample size must be between 1 and 1000.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div data-testid="explain-launcher-step-4">
              <div style={{
                padding: 16, borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--bg-secondary)', fontSize: 13, lineHeight: 1.6,
              }}>
                <div data-testid="explain-launcher-review-summary">
                  Method: <strong>SHAP</strong>.
                  <br />
                  Mode: <strong>{sourceMode === 'single' ? 'Single prediction' : 'Batch'}</strong>.
                  <br />
                  {sourceMode === 'single' && (
                    <>
                      Prediction: <code>{effectivePredictionId}</code>.
                      <br />
                    </>
                  )}
                  {sourceMode === 'batch' && (
                    <>
                      Batch: <code>{batchId.trim()}</code>.
                      <br />
                      Sample size: <strong>{sampleSize}</strong>.
                      <br />
                    </>
                  )}
                  Max features: <strong>{maxFeatures}</strong>.
                </div>
              </div>
            </div>
          )}
      </>
    </WizardModalShell>
  );
};

export default ExplainabilityLauncher;
